/**
 * The toast viewport the console's two per-person screens share.
 *
 * Only one thing on this board raises a toast — the record drawer's "Copied" —
 * but the drawer is reachable from both `/dynamics/investigate` and
 * `/dynamics/reports`, and `useToastManager` needs a `Toast.Provider` above it.
 * Rather than two copies of the same six lines, the provider and the viewport
 * live here and each page wraps its body in `ConsoleToasts`.
 *
 * Page-scoped rather than app-scoped on purpose: the shell has no toasts of its
 * own, and a viewport mounted for every board would be a portal waiting for a
 * message that never comes.
 */

import { Toast, useToastManager } from "@norican/react";
import type { ReactNode } from "react";

/** Wrap a page body that can open the record drawer. */
export function ConsoleToasts({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider>
      {children}
      <ToastStack />
    </Toast.Provider>
  );
}

function ToastStack() {
  const manager = useToastManager();
  return (
    <Toast.Portal>
      <Toast.Viewport>
        {manager.toasts.map((toast) => (
          <Toast.Root key={toast.id} toast={toast}>
            <Toast.Content>
              <Toast.Title>{toast.title}</Toast.Title>
              {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
            </Toast.Content>
            <Toast.CloseButton />
          </Toast.Root>
        ))}
      </Toast.Viewport>
    </Toast.Portal>
  );
}
